import React, { Fragment, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux';
import ApexCharts from 'apexcharts';
import 'react-notifications/lib/notifications.css';
import moment from "moment/moment";
import { getLiveTips } from "../redux/slices/live-tips";
import {
    FormGroup, Grid,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField
} from "@mui/material";
import Button from "@mui/material/Button";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import FunctionsIcon from '@mui/icons-material/Functions';

import './Dashboard.scss';

export default function Dashboard() {
    const dispatch = useAppDispatch();

    const { tips } = useAppSelector(state => state.live_tips);

    const [showTipsTable, setShowTipsTable] = useState(false); // Initial bet amount
    const [initialBet, setInitialBet] = useState(10); // Initial bet amount
    const [strategy, setStrategy] = useState('fixed'); // Betting strategy
    const [days, setDays] = useState(7); // Number of previous days to consider
    const [filteredTips, setFilteredTips] = useState([]); // Filtered tips based on selected days
    const [totalWinnings, setTotalWinnings] = useState(0); // To store calculated winnings
    const [winRate, setWinRate] = useState(0); // To store win rate
    const [bankrollHistory, setBankrollHistory] = useState([]); // To store the bankroll effect after each tip
    const [initialBankAmount, setInitialBankAmount] = useState(100); // Initial bank amount

    useEffect(() => {
        dispatch(getLiveTips());
    }, [dispatch]);

    const calculateWinPercentage = (data) => {
        // Group data by date
        const groupedData = data.filter((p) => p.status !== 'pending').reduce((result, item) => {
            const date = moment(item.timestamp).format("YYYY-MM-DD");
            if (!result[date]) {
                result[date] = { total: 0, wins: 0 };
            }
            result[date].total += 1;
            if (item.status === "win") {
                result[date].wins += 1;
            }
            return result;
        }, {});

        // Calculate win percentage for each date
        const winPercentageByDate = {};
        for (const date in groupedData) {
            const { total, wins } = groupedData[date];
            winPercentageByDate[date] = ((wins / total) * 100).toFixed(2);
        }

        // Sort dates in ascending order
        return Object.keys(winPercentageByDate)
            .sort()
            .reduce((sortedResult, date) => {
                sortedResult[date] = winPercentageByDate[date];
                return sortedResult;
            }, {});
    };

    // take only the tips from the last 15 days
    const tipsForLast15Days = tips.filter(tip => moment(tip.timestamp).isAfter(moment().subtract(15, 'days')));
    const winPercentage = calculateWinPercentage(tipsForLast15Days);
    const tipsPerDate = tipsForLast15Days.reduce((result, item) => {
        const date = moment(item.timestamp).format("YYYY-MM-DD");
        if (!result[date]) {
            result[date] = 0;
        }
        result[date] += 1;
        return result;
    }, {});

    const options = {
        chart: {
            height: "300px",
            maxWidth: "100%",
            type: "area",
            fontFamily: "Inter, sans-serif",
            animations: {
                enabled: false,
            }
        },
        tooltip: {
            enabled: true,
            x: {
                show: false,
            },
            y: {
                show: false,
            }
        },
        fill: {
            type: "gradient",
            gradient: {
                opacityFrom: 0.55,
                opacityTo: 0,
                shade: "var(--primary-color)",
                gradientToColors: ["var(--primary-color)"],
            },
        },
        series: [
            {
                name: "Win rate",
                data: Object.values(winPercentage),
                color: "var(--primary-color)",
            },
            {
                name: "Tips per day",
                data: Object.values(tipsPerDate).reverse(),
                color: "black",
            }
        ],
        xaxis: {
            categories: Object.keys(winPercentage).map((date) => moment(date).format("MMM DD")),
            labels: {
                show: true,
                rotate: -45,
                hideOverlappingLabels: true,
                showDuplicates: false,
                style: {
                    colors: Object.keys(winPercentage).map((date) => "var(--momentum-color)"),
                },
            },
        },
        yaxis: {
            labels: {
                show: false,
                formatter: (value) => `${value}`,
                style: {
                    colors: Object.keys(winPercentage).map((date) => "var(--momentum-color)"),
                },
            },
        }
    };

    useEffect(() => {
        const chart = new ApexCharts(document.getElementById("area-chart"), options);
        chart.render();

        return () => {
            chart.destroy();
        };
    }, [options]);

    useEffect(() => {
        filterTipsByDays();
    }, [tips, days]);

    // Function to filter tips based on the number of selected days
    const filterTipsByDays = () => {
        const filtered = tips.filter(tip => {
            const tipDate = moment(tip.timestamp);
            const today = moment();
            return tipDate.isBetween(today.clone().subtract(days, 'days'), today);
        });
        setFilteredTips(filtered);
    };

    const calculateWinnings = () => {
        let total = 0;
        let wins = 0;
        let currentBalance = initialBankAmount; // Use initial bank amount
        let previousBet = initialBet; // Initialize the previous bet for Martingale strategy
        let currentBet = initialBet; // Initialize the current bet for Reverse Martingale strategy
        let dAlembertBet = initialBet; // Initialize the D'Alembert bet amount
        let progressiveBet = initialBet; // Initialize the Progressive bet amount
        const history = [];
        const fibonacciSequence = [1, 1]; // Starting sequence for Fibonacci

        // Generate Fibonacci sequence up to a reasonable limit
        const generateFibonacci = (length) => {
            while (fibonacciSequence.length < length) {
                fibonacciSequence.push(fibonacciSequence[fibonacciSequence.length - 1] + fibonacciSequence[fibonacciSequence.length - 2]);
            }
        };

        filteredTips
            .sort((a, b) => moment(a.timestamp).unix() - moment(b.timestamp).unix())
            .forEach((tip, index) => {
                // Determine bet amount based on strategy
                const betAmount = (() => {
                    switch (strategy) {
                        case 'fixed': return initialBet;
                        case 'percentage': return currentBalance * 0.05;
                        case 'fibonacci': {
                            generateFibonacci(filteredTips.length);
                            return fibonacciSequence[index] || initialBet;
                        }
                        case 'martingale': {
                            // D'Alembert Strategy (dAlembert):
                            // On Win: Reset the bet to the initial amount.
                            // On Loss: Double the previous bet amount.
                            const amount = previousBet;
                            previousBet = tip.status === 'win' ? initialBet : previousBet * 2;
                            return amount;
                        }
                        case 'reverseMartingale': {
                            // For the Reverse Martingale strategy, also known as the Paroli system, the logic should be as follows:
                            // On a win: Increase the bet for the next round, usually by doubling it.
                            // On a loss: Reset the bet to the initial bet amount.
                            const amount = currentBet;
                            currentBet = tip.status === 'win' ? currentBet * 2 : initialBet;
                            return amount;
                        }
                        case 'dAlembert': {
                            // D'Alembert Strategy (dAlembert):
                            // On Win: Decrease the bet amount by one unit (or reset it to the minimum if it's already at the minimum).
                            // On Loss: Increase the bet amount by one unit.
                            const amount = dAlembertBet;
                            dAlembertBet = tip.status === 'win' ? Math.max(initialBet, dAlembertBet - 1) : dAlembertBet + 1;
                            return amount;
                        }
                        case 'kelly': {
                            // Kelly Criterion requires an estimate of the probability of winning

                            const probabilityOfWinning = estimateProbability(tip);
                            const odds = tip.odds / 100; // Convert odds to decimal
                            const kellyFraction = probabilityOfWinning * (odds + 1) - 1;
                            const betFraction = kellyFraction / odds;
                            return Math.max(0, Math.min(currentBalance, currentBalance * betFraction));
                        }
                        case 'valueBetting': {
                            const value = estimateValue(tip);
                            return value > 0 ? Math.min(currentBalance, value) : 0;
                        }
                        case 'arbitrage': return initialBet; // Arbitrage requires complex logic not covered here
                        case 'progressive': {
                            const amount = progressiveBet;
                            progressiveBet = tip.status === 'win' ? Math.max(initialBet, progressiveBet - 1) : progressiveBet + 1;
                            return amount;
                        }
                        default: return initialBet;
                    }
                })();

                // Ensure bet amount is not more than current balance
                const finalBetAmount = Math.min(betAmount, currentBalance);

                // Calculate winnings or losses
                const winnings = tip.status === 'win'
                    ? finalBetAmount * (tip.odds / 100) - finalBetAmount
                    : -finalBetAmount;

                // Update balance and statistics
                total += winnings;
                currentBalance += winnings;
                wins += tip.status === 'win' ? 1 : 0;

                history.push({
                    tipId: tip.id,
                    bankrollAfter: currentBalance,
                    betAmount: finalBetAmount.toFixed(2),
                    bankrollChange: winnings,
                });
            });

        setTotalWinnings(total);
        setWinRate((wins / filteredTips.length) * 100);
        setBankrollHistory(history);
    };


    // Example function for Kelly Criterion probability estimation
    const estimateProbability = (tip) => {
        // Your logic here to estimate probability based on the tip
        return 0.5; // Placeholder value
    };

    // Example function for Value Betting
    const estimateValue = (tip) => {
        // Your logic here to estimate value of the bet
        return 0; // Placeholder value
    };


    return (
        <div className='lg:px-10 px-3 h-full overflow-hidden dashboard-wrapper'>
            <div className="w-full bg-white rounded-lg shadow p-4 md:p-6"
                 style={{backgroundColor: "var(--card-background-color)"}}>
                <div className="flex justify-between">
                    <div className="leading-none text-xl font-bold pb-2" style={{color: "var(--momentum-color)"}}>
                        Daily win rate in percentage
                    </div>
                </div>

                <div id="area-chart">
                </div>
            </div>

            {/* New Inputs for Bet Simulation */}
            <div className="mt-6 w-full bg-white rounded-lg shadow p-4 md:p-6"
                 style={{backgroundColor: "var(--card-background-color)"}}>
                <div className="leading-none text-xl font-bold pb-2" style={{color: "var(--momentum-color)"}}>
                    Bet Simulation
                </div>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <TextField
                                label="Initial Bank Amount"
                                type="number"
                                value={initialBankAmount}
                                fullWidth
                                margin="dense"
                                onChange={(e) => setInitialBankAmount(parseFloat(e.target.value))}
                            />

                            <TextField
                                label="Bet Amount"
                                type="number"
                                value={initialBet}
                                style={{marginTop: "0.5rem"}}
                                fullWidth
                                margin="dense"
                                onChange={(e) => setInitialBet(parseFloat(e.target.value))}
                            />

                            <Select
                                value={strategy}
                                onChange={(e) => setStrategy(e.target.value)}
                                fullWidth
                                className="mt-2"
                                label="Betting Strategy"
                            >
                                <MenuItem value="fixed">Fixed Amount</MenuItem>
                                <MenuItem value="percentage">Percentage of Total Balance</MenuItem>
                                <MenuItem value="fibonacci">Fibonacci Sequence</MenuItem>
                                <MenuItem value="martingale">Martingale</MenuItem>
                                <MenuItem value="reverseMartingale">Reverse Martingale</MenuItem>
                                <MenuItem value="dAlembert">D’Alembert</MenuItem>
                                <MenuItem value="kelly">Kelly Criterion</MenuItem>
                                <MenuItem value="valueBetting">Value Betting</MenuItem>
                                <MenuItem value="arbitrage">Arbitrage</MenuItem>
                                <MenuItem value="progressive">Progressive Betting</MenuItem>
                            </Select>

                            <Select
                                label="Number of Previous Days"
                                value={days}
                                onChange={(e) => setDays(parseInt(e.target.value as string, 10))}
                                fullWidth
                                className="mt-2 mb-4"
                            >
                                <MenuItem value={1}>1 Day</MenuItem>
                                <MenuItem value={3}>3 Days</MenuItem>
                                <MenuItem value={7}>7 Days</MenuItem>
                                <MenuItem value={14}>14 Days</MenuItem>
                                <MenuItem value={30}>30 Days</MenuItem>
                                <MenuItem value={60}>60 Days</MenuItem>
                                <MenuItem value={90}>90 Days</MenuItem>
                            </Select>

                            <div className="text-center">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={calculateWinnings}
                                    className="mt-4"
                                >
                                    <FunctionsIcon style={{marginRight: "5px"}}/>
                                    Simulate
                                </Button>
                            </div>
                        </FormGroup>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <FormGroup>
                            <div className="MuiPaper-root MuiPaper-rounded MuiPaper-elevation1 MuiCard-root award-wrapper">
                                <div className="MuiCardMedia-root victory-image" role="img"
                                     style={{backgroundImage: "url(https://jkkm.info/ui/images/awards/victory.png)"}} />
                                <div className="MuiCardContent-root css-1qw96cp">
                                    <div className="MuiTypography-root MuiTypography-overline">
                                        <span>Total Winnings: </span>
                                        <span>{totalWinnings.toFixed(2)}$</span>
                                    </div>
                                    <div className="MuiTypography-root MuiTypography-overline">
                                        <span>Win Rate: </span>
                                        <span>{winRate.toFixed(2)}%</span>
                                    </div>

                                    {winRate !== 0 && (
                                        <div className="text-center mt-4">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => setShowTipsTable(!showTipsTable)}
                                            >
                                                {showTipsTable ? (
                                                    <>
                                                        <VisibilityOffIcon style={{marginRight: "5px"}}/>
                                                        Hide tips
                                                    </>
                                                ) : (
                                                    <>
                                                        <VisibilityIcon style={{marginRight: "5px"}}/>
                                                        Show tips
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </FormGroup>
                    </Grid>
                </Grid>
            </div>

            {/* Table of Tips */}

            {
                showTipsTable &&
                <TableContainer component={Paper} className="mt-6 mb-6">
                    <Table aria-label="tips table">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">Date</TableCell>
                                <TableCell align="center">Title</TableCell>
                                <TableCell align="center">Home Team</TableCell>
                                <TableCell align="center">Away Team</TableCell>
                                <TableCell align="center">Market</TableCell>
                                <TableCell align="center">Pick</TableCell>
                                <TableCell align="center">Odds</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Final Score</TableCell>
                                <TableCell align="center">Bet Amount</TableCell>
                                <TableCell align="center">Bankroll Effect</TableCell> {/* New column */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTips.sort((a, b) => moment(b.timestamp).unix() - moment(a.timestamp).unix()).map((tip) => {
                                const bankrollData = bankrollHistory.find(history => history.tipId === tip.id);
                                return (
                                    <TableRow key={tip.id}>
                                        <TableCell align="center">{moment(tip.timestamp).format('DD MMM, HH:mm')}</TableCell>
                                        <TableCell align="center">{tip.title}</TableCell>
                                        <TableCell align="center">{tip.games[0].home}</TableCell>
                                        <TableCell align="center">{tip.games[0].away}</TableCell>
                                        <TableCell align="center">{tip.games[0].market}</TableCell>
                                        <TableCell align="center">{tip.games[0].pick}</TableCell>
                                        <TableCell align="center">{(tip.odds / 100).toFixed(2)}</TableCell>
                                        <TableCell align="center"
                                                   style={{color: tip.status === 'win' ? 'var(--won-color)' : 'var(--lost-color)'}}>
                                            {tip.status}
                                        </TableCell>
                                        <TableCell align="center">{tip.games[0].final_score}</TableCell>
                                        <TableCell align="center">{bankrollData?.betAmount}</TableCell>
                                        <TableCell align="center">
                                        <span
                                            style={{color: bankrollData?.bankrollAfter > 0 ? 'var(--won-color)' : 'var(--lost-color)'}}>
                                            {bankrollData?.bankrollAfter.toFixed(2)}
                                        </span>
                                            <span> </span>
                                            <span style={{color: bankrollData?.bankrollChange > 0 ? 'var(--won-color)' : 'var(--lost-color)'}}>
                                             ({bankrollData?.bankrollChange > 0 ? '+' : ''}{bankrollData?.bankrollChange.toFixed(2)})
                                        </span>
                                        </TableCell> {/* Bankroll effect column */}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </div>
    );
}
